import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import Navigation from './components/Navigation';
import AppContent from './AppContent';
import SplashScreen from './components/SplashScreen';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';
import ScrollToTop from './components/ScrollToTop';
import API_BASE_URL from './config';
import './App.css';

function AppContentWrapper() {
  const location = useLocation();
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [tracks, setTracks] = useState([]);
  const backgroundAudioRef = useRef(null);

  const fetchTracks = useCallback(async () => {
    try {
      // Используем API_BASE_URL из config.js
      const response = await fetch(`${API_BASE_URL}/api/tracks`);
      const data = await response.json();
      setTracks(data);
      
      // Автоматически выбираем первый трек если есть треки и трек еще не выбран
      if (data && data.length > 0 && !currentTrack) {
        setCurrentTrack(data[0]);
      }
    } catch (error) {
      console.error('Error fetching tracks:', error);
    }
  }, [currentTrack]);

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  // Управление фоновым воспроизведением на других страницах
  useEffect(() => {
    const audio = backgroundAudioRef.current;
    if (!audio || !currentTrack) return;

    const isPlayerPage = location.pathname === '/player' || location.pathname === '/';
    
    if (!isPlayerPage) {
      // Используем URL трека напрямую (backend уже возвращает правильный путь)
      // Используем API_BASE_URL из config.js
      audio.src = currentTrack.url.startsWith('http') ? currentTrack.url : `${API_BASE_URL}${currentTrack.url}`;
      audio.load();
      
      if (isPlaying) {
        audio.play().catch(err => {
          console.debug('Background audio play error (ignored):', err);
        });
      }
    } else {
      audio.pause();
      audio.src = '';
    }
  }, [currentTrack, location.pathname, isPlaying]);

  useEffect(() => {
    const audio = backgroundAudioRef.current;
    if (!audio) return;

    const isPlayerPage = location.pathname === '/player' || location.pathname === '/';
    if (isPlayerPage) return;

    if (isPlaying) {
      audio.play().catch(err => {
        console.debug('Background audio play error (ignored):', err);
      });
    } else {
      audio.pause();
    }
  }, [isPlaying, location.pathname]);

  const handleTrackSelect = (track, shouldPlay = true) => {
    // КРИТИЧНО: Проверяем, что трек имеет поле project
    if (!track || !track.project) {
      console.warn('Track does not have project field');
      return;
    }
    
    setCurrentTrack(track);
    if (shouldPlay) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  };

  const handleTrackEnd = () => {
    setIsPlaying(false);
    handleNextTrack();
  };

  const handleNextTrack = () => {
    if (!tracks || tracks.length === 0 || !currentTrack) return;
    
    // КРИТИЧНО: Фильтруем треки по проекту текущего трека
    // Это гарантирует, что треки из разных проектов не пересекаются
    const currentProject = currentTrack.project;
    const filteredTracks = tracks.filter(t => t.project === currentProject);
    
    if (filteredTracks.length === 0) return;
    
    const currentIndex = filteredTracks.findIndex(t => t.id === currentTrack.id);
    if (currentIndex === -1) {
      // Текущий трек не в отфильтрованном списке, выбираем первый трек из списка
      setCurrentTrack(filteredTracks[0]);
      setIsPlaying(true);
      return;
    }
    
    // Если мы на последнем треке, останавливаем воспроизведение
    if (currentIndex === filteredTracks.length - 1) {
      setIsPlaying(false);
      return;
    }
    
    const nextIndex = currentIndex + 1;
    const nextTrack = filteredTracks[nextIndex];
    
    // КРИТИЧНО: Проверяем, что следующий трек соответствует проекту текущего трека
    if (nextTrack.project !== currentProject) {
      setIsPlaying(false);
      return;
    }
    
    setCurrentTrack(nextTrack);
    setIsPlaying(true);
  };

  const handlePreviousTrack = () => {
    if (!tracks || tracks.length === 0 || !currentTrack) return;
    
    // КРИТИЧНО: Фильтруем треки по проекту текущего трека
    // Это гарантирует, что треки из разных проектов не пересекаются
    const currentProject = currentTrack.project;
    const filteredTracks = tracks.filter(t => t.project === currentProject);
    
    if (filteredTracks.length === 0) return;
    
    const currentIndex = filteredTracks.findIndex(t => t.id === currentTrack.id);
    if (currentIndex === -1) {
      // Текущий трек не в отфильтрованном списке, выбираем последний трек из списка
      setCurrentTrack(filteredTracks[filteredTracks.length - 1]);
      setIsPlaying(true);
      return;
    }
    
    // Если мы на первом треке, останавливаем воспроизведение
    if (currentIndex === 0) {
      setIsPlaying(false);
      return;
    }
    
    const previousIndex = currentIndex - 1;
    const previousTrack = filteredTracks[previousIndex];
    
    // КРИТИЧНО: Проверяем, что предыдущий трек соответствует проекту текущего трека
    if (previousTrack.project !== currentProject) {
      setIsPlaying(false);
      return;
    }
    
    setCurrentTrack(previousTrack);
    setIsPlaying(true);
  };

  return (
    <>
      <AppContent 
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        tracks={tracks}
        onTrackSelect={handleTrackSelect}
        onPlayPause={setIsPlaying}
        onTrackEnd={handleTrackEnd}
        onNextTrack={handleNextTrack}
        onPreviousTrack={handlePreviousTrack}
      />
      
      {/* Скрытый audio элемент для фонового воспроизведения на других страницах */}
      <audio
        ref={backgroundAudioRef}
        onEnded={handleTrackEnd}
        style={{ display: 'none' }}
      />
    </>
  );
}

function App() {
  const [selectedProject, setSelectedProject] = useState('commercial');
  const [showSplash, setShowSplash] = useState(true);

  // Проверяем, был ли splash screen уже показан в этой сессии
  useEffect(() => {
    const splashShown = sessionStorage.getItem('splashShown');
    if (splashShown === 'true') {
      setShowSplash(false);
    }
  }, []);

  const handleSplashComplete = () => {
    sessionStorage.setItem('splashShown', 'true');
    setShowSplash(false);
  };

  return (
    <LanguageProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div className="App">
          <ScrollToTop />
          {showSplash ? (
            <SplashScreen onComplete={handleSplashComplete} />
          ) : (
            <>
              <Navigation 
                selectedProject={selectedProject}
                onProjectChange={setSelectedProject}
              />
              <AppContentWrapper />
              <Footer />
              <ChatWidget />
            </>
          )}
        </div>
      </Router>
    </LanguageProvider>
  );
}

export default App;

